import moment from "moment-timezone";
import {
  DATE_FORMATS,
  get45MinIntervals,
  isTodayBeforeCurrentTime,
} from "../../../services/utils/date-and-time.utils";
import { VendorSlot } from "../vendor-slot-selection/types";

export type TimesByDate = Record<string, Date[]>;

export function groupAvailableTimesByDate(
  availableDatesArr: unknown[] | undefined,
  pastOnly: boolean
): TimesByDate {
  if (!Array.isArray(availableDatesArr)) return {};

  const grouped: TimesByDate = {};

  availableDatesArr.forEach((slotTime) => {
    const date =
      slotTime instanceof Date
        ? slotTime
        : new Date(slotTime as string | number);
    const isPast = isTodayBeforeCurrentTime(date);

    if (pastOnly ? !isPast : isPast) return;

    const key = moment(date).format(DATE_FORMATS.yyymmdd);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(date);
  });

  Object.keys(grouped).forEach((key) => {
    grouped[key].sort((a, b) => a.getTime() - b.getTime());
  });

  return grouped;
}

export const buildSuggestedTimeSlotsPayload = (
  slots: VendorSlot[],
  clientTimezoneValue?: string
) => {
  const slotsToConvert = slots
    .filter((slot) => slot.selectedSlotTimeFrom && slot.selectedSlotTimeTo)
    .map((slot) => {
      if (clientTimezoneValue) {
        const convertedFrom = moment(slot.selectedSlotTimeFrom).tz(
          clientTimezoneValue
        );
        const convertedTo = moment(slot.selectedSlotTimeTo).tz(
          clientTimezoneValue
        );
        return {
          from: convertedFrom.toDate(),
          to: convertedTo.toDate(),
          isTimeRange: slot.isTimeRange,
        };
      }

      return {
        from: slot.selectedSlotTimeFrom,
        to: slot.selectedSlotTimeTo,
        isTimeRange: slot.isTimeRange,
      };
    });

  const availableTimeSlots = Array.from(
    new Set(
      slotsToConvert.flatMap(({ from, to, isTimeRange }) =>
        get45MinIntervals(from, to, isTimeRange)
      )
    )
  ).map((dateString) => ({
    from: dateString,
    to: new Date(new Date(dateString).getTime() + 45 * 60000),
  }));

  const uniqueSlots = availableTimeSlots.filter((timeSlot, index, self) => {
    const idx = self.findIndex(
      (t) =>
        new Date(t.from).getDate() === new Date(timeSlot.from).getDate() &&
        new Date(t.from).getHours() === new Date(timeSlot.from).getHours() &&
        new Date(t.from).getMinutes() === new Date(timeSlot.from).getMinutes()
    );
    return idx === index;
  });

  uniqueSlots.sort((a, b) => a.from - b.from);

  return uniqueSlots.map((slot) => ({
    from: new Date(slot.from).toUTCString(),
    to: new Date(slot.to).toUTCString(),
  }));
};
